import {Product} from '@shopify/hydrogen/storefront-api-types';
import {normalizeRebuyProducts} from '~/lib/normalize/normalizeRebuyProducts';
import {
  ProductCarouselEntity,
  ProductGroupEntity,
} from '~/types/ProductCarouselEntity';
import {RebuyProductsEntity} from '~/types/RebuyProductsEntity';
import {ProductCarousel} from './ProductCarousel.client';
import {useEffect, useState} from 'react';

const REBUY_API_URL = import.meta.env.PUBLIC_REBUY_API_URL;
const REBUY_API_KEY = import.meta.env.PUBLIC_REBUY_API_KEY;

export function ProductCarouselContainer({
  title,
  icon,
  description,
  productGroups,
  shopifyProductID,
  disableMargin,
}: ProductCarouselEntity & {
  shopifyProductID?: string;
  disableMargin?: boolean;
}) {
  const [rebuyRequestMap, setRebuyRequestMap] = useState<
    Record<string, Product[]>
  >({});
  const [isLoading, setLoading] = useState(true);
  const [isError, setError] = useState(false);

  useEffect(() => {
    setLoading(true);
    requestRebuy(productGroups, shopifyProductID)
      .then((data) => {
        setRebuyRequestMap(data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, [productGroups, shopifyProductID]);

  if (isError) {
    return null;
  }

  return (
    <ProductCarousel
      _type="productCarousel"
      title={title}
      icon={icon}
      description={description}
      productGroups={productGroups}
      rebuyRequestMap={rebuyRequestMap}
      isLoading={isLoading}
      disableMargin={disableMargin}
    />
  );
}

async function requestRebuy(
  productGroups: ProductGroupEntity[],
  shopifyProductID: string | undefined,
) {
  const rebuyRequestMap: Record<string, Product[]> = {};

  for (const productGroup of productGroups) {
    if (
      productGroup.rebuyCustomID &&
      // Don't request the same rebuy widget twice, if there is any case like that.
      !rebuyRequestMap[productGroup.rebuyCustomID]
    ) {
      const request = await fetch(
        `${REBUY_API_URL}/custom/id/${
          productGroup.rebuyCustomID
        }?key=${REBUY_API_KEY}&limit=10${
          shopifyProductID
            ? `&shopify_product_ids=${shopifyProductID.split('/').pop()}`
            : ''
        }&metafields=yes`,
      );

      const response: RebuyProductsEntity = await request.json();

      if (response.data && response.data.length > 0) {
        rebuyRequestMap[productGroup.rebuyCustomID] = normalizeRebuyProducts(
          response.data,
        );
      } else {
        throw new Error(
          `Rebuy API returned no data for custom ID: ${productGroup.rebuyCustomID}`,
        );
      }
    }
  }

  return rebuyRequestMap;
}
